import { AnimatePresence, MotionConfig, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import "./equivalents.css";
import { equivalentImage, type Equivalent } from "./pick";

// The 3D item on its own: pops in with a spring when scrolled into view and when the item changes, and floats over a
// breathing shadow. The idle loops are CSS (equivalents.css) so they run from first paint and stop for reduced motion;
// MotionConfig drops the spring transforms for reduced motion without changing the server-rendered styles.
// className goes on a plain wrapper so callers' opacity and scale classes are not overridden by framer's inline style.
export function EquivalentArt({ equivalent, size = 72, className }: { equivalent: Equivalent; size?: number; className?: string }) {
  // size drives the pop distance; responsive callers can override the box with !size-* classes
  const src = equivalentImage(equivalent);
  return (
    <span className={cn("pointer-events-none relative inline-block shrink-0 select-none", className)} style={{ width: size, height: size }} aria-hidden="true">
      <MotionConfig reducedMotion="user">
        <motion.span
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 0.5, rotate: -14, y: size * 0.18 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ type: "spring", stiffness: 220, damping: 15, mass: 0.9 }}
        >
          <span className="absolute inset-x-0 bottom-[4%] mx-auto h-[9%] w-[58%] rounded-[50%] bg-[rgb(5_9_45/0.3)] blur-[3px] motion-safe:animate-[eq-shadow_3.6s_ease-in-out_infinite]" />
          <span className="absolute inset-0 motion-safe:animate-[eq-float_3.6s_ease-in-out_infinite]">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.span
                key={equivalent.item.slug}
                className="absolute inset-0"
                initial={{ opacity: 0, scale: 0.4, rotate: -18, y: size * 0.2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
                exit={{ opacity: 0, scale: 0.6, rotate: 14, y: -size * 0.1 }}
                transition={{ type: "spring", stiffness: 260, damping: 16, mass: 0.8 }}
              >
                <img src={src} alt="" width={size} height={size} className="absolute inset-0 size-full object-contain drop-shadow-[0_6px_10px_rgba(5,9,45,0.18)]" loading="lazy" decoding="async" />
              </motion.span>
            </AnimatePresence>
          </span>
        </motion.span>
      </MotionConfig>
    </span>
  );
}

// "به قیمت یه همبرگر" with the item emphasised in the display face; slides when the item changes. Used next to
// EquivalentArt in plan pickers; callers size the item with [&>b]: classes.
export function EquivalentLine({ equivalent, className }: { equivalent: Equivalent; className?: string }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const label = equivalent.item.name;
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={label}
        className={cn("inline-block text-ink-2", className)}
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        {t("ui.landing.plans.equivalent", { item: "" })}
        <b className="font-display font-extrabold text-ink">{label}</b>
      </motion.span>
    </AnimatePresence>
  );
}
